import { Drawer, Typography, Tooltip, Button, Collapse } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useSchedule } from '../ScheduleContext'
import JobDrawerSummary from './JobDrawerSummary'
import JobDrawerJobOverview from './JobDrawerJobOverview'
import JobDrawerVisitNotes from './JobDrawerVisitNotes'
import JobDrawerVisitStatuses from './JobDrawerVisitStatuses'
import JobDrawerWorkedCrew from './JobDrawerWorkedCrew'
import RemoveButton from './RemoveButton'
import OpenJobLink from './OpenJobLink'
import { useGetRepeatingEventConfigQuery, useRemoveScheduledEventMutation, useRemoveScheduledEventsByRangeMutation, useRemoveScheduledEventsMutation } from '../scheduleQueries'
import EditVisitModal from '../../visits/EditVisitModal'
import { useState, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { getVisitForEvent, getVisitsForEvent, getVisitRemovedWithEvent, DateRange, clampToDateRange } from '../scheduleModel'
import { ScheduledEvent, VisitWorkareaStatusRecord, Timesheet, CalendarEventVisit, Visit, CrewMember } from '../scheduleApi'
import { findCrewMemberByEmail } from '../crewModel'

const { Title, Text } = Typography

export type JobDrawerEventData = {
  event: ScheduledEvent
  calendarEventVisits: CalendarEventVisit[]
  visits: Visit[]
  visitWorkareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
}

type Props = {
  open: boolean
  onClose: () => void
  eventData: JobDrawerEventData | null
  date: Dayjs
  dateRange: DateRange
  readOnly?: boolean
}

const JobDrawer = ({ open, onClose, eventData, date, dateRange, readOnly }: Props) => {
  const { crew } = useSchedule()
  const { mutate: removeScheduledEvent } = useRemoveScheduledEventMutation()
  const { mutate: removeScheduledEvents } = useRemoveScheduledEventsMutation()
  const { mutate: removeScheduledEventsByRange } = useRemoveScheduledEventsByRangeMutation()

  const event = eventData?.event ?? null
  const visits = eventData?.visits ?? []
  const calendarEventVisits = eventData?.calendarEventVisits ?? []
  const workareaStatuses = eventData?.visitWorkareaStatuses ?? []
  const timesheets = eventData?.timesheets ?? []

  const { data: repeatingEventConfig } = useGetRepeatingEventConfigQuery(open && !readOnly ? event?.repeatingId || null : null)
  const [editVisitModalOpen, setEditVisitModalOpen] = useState(false)
  const [editVisitId, setEditVisitId] = useState<string | null>(null)

  const visit = event ? getVisitForEvent(event, calendarEventVisits, visits, dateRange) : null
  const allVisits = useMemo(() => {
    if (!event) {
      return []
    }
    return getVisitsForEvent(event, calendarEventVisits, visits)
  }, [event, calendarEventVisits, visits])

  if (!event) {
    return null
  }

  const assignedCrew = event.crewMemberIds
    ? event.crewMemberIds?.split(',')
      .map(id => crew?.find(c => c.id === parseInt(id)))
      .filter((member): member is CrewMember => member !== undefined)
    : []

  const crewLead = event.crewForemanEmail
    ? findCrewMemberByEmail(crew, event.crewForemanEmail)
    : null

  const allCrew = [...(crewLead ? [crewLead] : []), ...assignedCrew.filter(c => c.id !== crewLead?.id)]

  return <>
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Title level={4} style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {event.jobNumber && <Text type="secondary" style={{ marginRight: 8, fontSize: 'inherit' }}>{event.jobNumber}</Text>}
                {event.jobName}
              </Title>
              {event.jobId ? <OpenJobLink jobId={event.jobId} /> : null}
            </div>
            {(() => {
              const fullText = `${event.customerDisplayName || 'Unknown Client'} ${event.customerAddress1 && event.customerCity && event.customerState && event.customerZip ? `• ${event.customerAddress1}, ${event.customerCity}, ${event.customerState} ${event.customerZip}` : ''}`
              const displayText = fullText.length > 40 ? fullText.substring(0, 40) + '...' : fullText
              const isTextTruncated = fullText.length > 40

              const textComponent = (
                <Text style={{
                  color: '#666',
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block'
                }}>
                  {displayText}
                </Text>
              )

              return isTextTruncated ? (
                <Tooltip title={fullText} placement="bottom">
                  {textComponent}
                </Tooltip>
              ) : textComponent
            })()}
          </div>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={500}
      closeIcon={false}
      extra={<CloseOutlined onClick={onClose} style={{ fontSize: '18px', cursor: 'pointer' }} />}
      styles={{
        body: { backgroundColor: '#fafbfc', padding: 0, display: 'flex', flexDirection: 'column', height: '100vh' },
        header: { backgroundColor: 'white' }
      }}
    >
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px',
        backgroundColor: '#fafbfc',
        paddingBottom: (!visit && !allVisits.length) ? '80px' : '24px'
      }}>
        <JobDrawerSummary
          event={event}
          allCrew={allCrew}
          repeatingEventConfig={repeatingEventConfig || null}
          readOnly={readOnly}
          selectedDate={clampToDateRange(date, { start: dayjs(event.start), end: dayjs(event.end) })} />
        <JobDrawerJobOverview event={event} />
        {allVisits.length > 0 ? (
          <>
            <div style={{ marginBottom: '8px' }}>
              <Text style={{ fontSize: '14px' }}>Visits</Text>
            </div>
            <Collapse
              accordion
              defaultActiveKey={[allVisits[0].id]}
              items={allVisits.map(v => ({
                key: v.id,
                label: dayjs(v.dateUtc).format('ddd • MMM D YYYY'),
                children: (
                  <>
                    <JobDrawerVisitStatuses event={event} visit={v} workareaStatuses={workareaStatuses} />
                    <JobDrawerWorkedCrew
                      event={event}
                      jobId={event.jobId}
                      timesheets={timesheets.filter(t => t.timecardId === v.id)}
                      crew={crew ?? []}
                      crewLeadEmail={event.crewForemanEmail}
                      workareaStatuses={workareaStatuses.filter(ws => ws.visitId === v.id)}
                      onManageClick={() => {
                        setEditVisitId(v.id)
                        setEditVisitModalOpen(true)
                      }}
                    />
                    <JobDrawerVisitNotes visit={v} />
                  </>
                ),
              }))}
            />
          </>
        ) : (
          <>
            {visit &&
              <JobDrawerVisitStatuses event={event} visit={visit} workareaStatuses={workareaStatuses} />}
            {visit &&
              <JobDrawerWorkedCrew
                event={event}
                jobId={event.jobId}
                timesheets={timesheets.filter(t => t.timecardId === visit.id)}
                crew={crew ?? []}
                crewLeadEmail={event.crewForemanEmail}
                workareaStatuses={workareaStatuses.filter(ws => ws.visitId === visit.id)}
                onManageClick={() => setEditVisitModalOpen(true)}
              />}
            {visit &&
              <JobDrawerVisitNotes visit={visit} />}
          </>
        )}

        {!readOnly && (
          <RemoveButton
            event={event}
            visitRemovedWithEvent={getVisitRemovedWithEvent(event, allVisits)}
            onVisitRemoved={onClose}
            onRemove={(option, dateRange) => {
              if (option === "this-event") {
                removeScheduledEvent(event.id)
              } else if (option === "this-and-future") {
                removeScheduledEvents(event.id)
              } else if (option === "selected-dates") {
                removeScheduledEventsByRange({ eventId: event.id, request: { start: dateRange?.[0]?.toISOString() || null, end: dateRange?.[1]?.toISOString() || null, jobId: event.jobId } })
              }
              onClose()
            }} />
        )}
      </div>

      {!readOnly && !visit && !allVisits.length && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fafbfc',
          padding: '16px 24px',
          borderTop: '1px solid #e8e8e8'
        }}>
          <Button
            type="primary"
            block
            size="large"
            onClick={() => {
              setEditVisitModalOpen(true)
            }}
            style={{
              height: '48px',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            Record Visit
          </Button>
        </div>
      )}
    </Drawer>
    {editVisitModalOpen && <EditVisitModal
      isOpen={editVisitModalOpen}
      visitId={editVisitId ?? ''}
      event={!editVisitId ? event : undefined}
      onCloseModal={() => {
        setEditVisitModalOpen(false)
        setEditVisitId(null)
      }}
      googleMapsApiKey={(window as any).env.GOOGLE_MAPS_API_KEY}
      defaultLunchBreakMinutes={0}
    />}
  </>
}

export default JobDrawer